import { useLocation, useNavigate } from 'react-router-dom'

// ============================================================================
// DELIBERATELY ABSENT FROM MOBILE — docs/design/handoff-mobile/README.md
// §Overview: "Journal index and day pages, Lecture study mode, Learning tracks,
// Interests library, Search, Connections/settings. Those stay on desktop."
//
// This is what a deep link to one of them lands on, and it is a real screen
// rather than a redirect for one reason: a URL that silently bounces to Today is
// indistinguishable from a broken link, and the owner would spend the next
// minute wondering whether the thing he saved is gone. It is not gone; it is on
// the desktop, and the route is still the route it is on.
//
// It is NOT an apology and NOT a "coming soon". These screens are absent by
// decision, not by backlog — the phone's job is capture, and adding a second
// place to edit is the thing the whole mobile brief is arranged against.
//
// TWO THINGS HAVE BEEN TAKEN BACK OFF THIS LIST, and both for the same reason:
// the phone turned out to be the only place the job could be done.
//
// `/journal` and `/journal/:date` (JRN-1c). The rule this list protects is that
// the phone does not become a second place to BROWSE, and a month list of days
// is not browsing — it is the owner, who journals from the phone daily, being
// able to read or fix yesterday from the device he wrote it on. The rest of the
// journal screen (heat map, tag counts, streak charts, search) is still the
// desktop's and is still absent here.
//
// `/settings/notifications`. Not because
// the rule softened, but because a push subscription belongs to the browser it
// was made in — the desktop physically cannot turn notifications on for the
// phone. The `/settings` entry below says so rather than making a claim that is
// no longer true, since a screen whose whole job is to be honest about where
// things live cannot be the one screen that is out of date.
//
// AND A THIRD, `/calendar` (DAY-1). This list used to carry "The calendar grid",
// and the sentence under it was true about a grid: a week of blocks that are
// dragged and resized needs a pointer and a week's worth of width. It was not
// true about READING the day — and while it stood, everything the owner put on
// his calendar was invisible on the device he carries. The phone now answers
// `/calendar` with one day as a list (screens/MobileCalendarDay.tsx). The week
// grid did not move, and nothing here claims it did; there is simply no longer a
// route pointed at this screen that would have to explain it.
// ============================================================================

/** What each absent route is, and where it lives. Keyed by path prefix. */
const ABSENT: Array<{ prefix: string; name: string; why: string }> = [
  {
    prefix: '/learning',
    name: 'Learning tracks',
    why: 'A track page is a curriculum, a pace chart and a session log side by side.',
  },
  {
    prefix: '/interests',
    name: 'The Interests library',
    why: 'Saving to it works from the capture sheet on this phone. Browsing it does not.',
  },
  {
    prefix: '/search',
    name: 'Search',
    why: 'One bar over five domains, with a literal block and a related block that must never be interleaved — two columns of reading.',
  },
  {
    prefix: '/settings',
    // NOT "settings", because one settings screen is on this phone: Notifications
    // (`/settings/notifications`, the bell in the Today band). A Web Push subscription belongs to
    // the browser it was made in, so that switch has to be pressable here — and this sentence
    // would be a false claim if it kept saying every setting is on the desk.
    name: 'Connections and the rest of settings',
    why: 'The trust surface reports on four backends at once. Changing what the app connects to is a desk decision. Notifications are the exception and are on this phone — the bell on Today, because a browser can only be subscribed to push from itself.',
  },
]

export function DesktopOnly() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const match = ABSENT.find((a) => pathname.startsWith(a.prefix))

  return (
    <div className="flex min-h-0 flex-1 flex-col justify-center gap-3 px-[26px]">
      <h1 className="text-[19px] font-semibold leading-[1.15] tracking-[-0.02em] text-ink">
        {match?.name ?? 'That screen'} is on the desktop
      </h1>
      <p className="text-[13.5px] font-normal leading-[1.6] text-ink-3 text-pretty">
        {match?.why ??
          'The phone is deliberately a smaller product: write, record, and read back a summary.'}{' '}
        Nothing is missing and nothing was lost — this address opens it on a wider screen.
      </p>
      <p className="text-[11.5px] font-normal leading-[1.5] text-microcopy">
        <span className="font-medium">{pathname}</span>
      </p>
      <button
        type="button"
        onClick={() => navigate('/')}
        className="mt-1 flex h-11 items-center justify-center self-start rounded-tile bg-ink px-5 text-[13.5px] font-semibold leading-none text-surface active:bg-[color-mix(in_srgb,var(--color-ink),black_30%)]"
      >
        Back to Today
      </button>
    </div>
  )
}
